import { useSyncExternalStore } from 'react';

/**
 * The design systems a portal can render in.
 *
 * - `classic` — the original identity (forest/cream, Geist), `styles/tokens.css`. The default.
 * - `site` — the public site's identity (TMR blue, Inter Tight + Manrope), `themes/site.css`.
 */
export type DesignTheme = 'classic' | 'site';

/** The `<html>` attribute every theme stylesheet is scoped under. */
export const DESIGN_THEME_ATTRIBUTE = 'data-ds';

/** The query parameter that picks a theme for this browser (`?ds=site`, `?ds=classic`). */
export const DESIGN_THEME_PARAM = 'ds';

/** Where the browser keeps the chosen theme. A UI preference — never a credential. */
export const DESIGN_THEME_STORAGE_KEY = 'tmr.design-system';

const isDesignTheme = (value: unknown): value is DesignTheme => value === 'classic' || value === 'site';

/** What {@link resolveDesignTheme} decides from. Plain values, so the rule is testable without a DOM. */
export interface DesignThemeSources {
  /** The page's query string (`location.search`). */
  search?: string;
  /** The value previously saved in storage, if any. */
  stored?: string | null;
  /** The portal's default — typically its `VITE_DESIGN_SYSTEM`. Absent or unknown means `classic`. */
  fallback?: string;
}

/**
 * Decides which theme to render: the URL wins, then the saved preference, then the portal's default,
 * then `classic`. An unknown value at any step is skipped rather than trusted.
 *
 * @param sources The URL, the stored preference and the portal default.
 * @returns The theme to apply.
 */
export function resolveDesignTheme({ search = '', stored = null, fallback }: DesignThemeSources): DesignTheme {
  const fromUrl = new URLSearchParams(search).get(DESIGN_THEME_PARAM);
  if (isDesignTheme(fromUrl)) return fromUrl;
  if (isDesignTheme(stored)) return stored;
  if (isDesignTheme(fallback)) return fallback;
  return 'classic';
}

// Storage can throw (private windows, blocked site data); a theme is never worth breaking the page for.
function readStored(): string | null {
  try {
    return window.localStorage.getItem(DESIGN_THEME_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(theme: DesignTheme): void {
  try {
    window.localStorage.setItem(DESIGN_THEME_STORAGE_KEY, theme);
  } catch {
    // The theme still applies for this visit; it just will not be remembered.
  }
}

const listeners = new Set<() => void>();

function currentTheme(): DesignTheme {
  if (typeof document === 'undefined') return 'classic';
  const value = document.documentElement.getAttribute(DESIGN_THEME_ATTRIBUTE);
  return isDesignTheme(value) ? value : 'classic';
}

/**
 * Sets the theme on `<html>` and remembers it, notifying every {@link useDesignTheme}.
 *
 * @param theme The theme to switch to.
 */
export function setDesignTheme(theme: DesignTheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute(DESIGN_THEME_ATTRIBUTE, theme);
  writeStored(theme);
  listeners.forEach((listener) => listener());
}

/**
 * Resolves the theme for this visit and applies it to `<html>`. Call it once, before the first render,
 * so the page never paints in one theme and flips to the other.
 *
 * A theme picked through the URL is saved, so the choice survives the redirects of a sign-in.
 *
 * @param fallback The portal's default theme (e.g. `import.meta.env.VITE_DESIGN_SYSTEM`).
 * @returns The theme that was applied.
 */
export function applyDesignTheme(fallback?: string): DesignTheme {
  if (typeof window === 'undefined') return 'classic';
  const theme = resolveDesignTheme({ search: window.location.search, stored: readStored(), fallback });
  setDesignTheme(theme);
  return theme;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * The theme on screen, re-rendering when {@link setDesignTheme} changes it.
 *
 * @returns The current theme.
 */
export function useDesignTheme(): DesignTheme {
  return useSyncExternalStore(subscribe, currentTheme, () => 'classic');
}
